import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { projectShowPath } from "@/lib/project-configs"
import type { Project, SharedProps, ProjectTag, PropertyType, ProjectStatus, PipeStatusRecord, ProjectOrigin } from "@/types"

type Props = {
  project: Project
  organisations: { id: string; name: string }[]
  users: { id: string; first_name: string; last_name: string }[]
  teams: { id: string; name: string }[]
  project_tags: ProjectTag[]
  pipe_statuses: PipeStatusRecord[]
}

function ProjectEdit({ project, organisations, users, teams, project_tags, pipe_statuses }: Props) {
  const { current_user } = usePage<SharedProps>().props

  const { data, setData, patch, processing } = useForm({
    name: project.name,
    property_type: project.property_type,
    address: project.address || "",
    zip_code: project.zip_code || "",
    city_name: project.city_name || "",
    city_code: project.city_code || "",
    country: project.country || "FR",
    email: project.email || "",
    phone_number: project.phone_number || "",
    status: project.status,
    pipe_status_id: project.pipe_status_id,
    lead_score: project.lead_score,
    house_surface: project.house_surface ? String(project.house_surface) : "",
    field_surface: project.field_surface ? String(project.field_surface) : "",
    origin: project.origin,
    organisation_id: project.organisation_id,
    assigned_user_ids: project.assigned_users?.map((u) => u.id) || [],
    assigned_team_ids: project.assigned_teams?.map((t) => t.id) || [],
    project_tag_ids: project.project_tags?.map((t) => t.id) || [],
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/projects/${project.id}`)
  }

  const toggleArrayItem = (field: "assigned_user_ids" | "assigned_team_ids" | "project_tag_ids", id: string) => {
    const current = data[field]
    setData(field, current.includes(id) ? current.filter((v) => v !== id) : [...current, id])
  }

  return (
    <>
      <Head title={`Modifier ${project.name}`} />
      <PageHeader title={`Modifier ${project.name}`} back={projectShowPath(project)} />
      <div className="space-y-6">
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Informations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du projet</Label>
                  <Input id="name" value={data.name} onChange={(e) => setData("name", e.target.value)} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="property_type">Type de bien</Label>
                  <Select value={data.property_type} onValueChange={(v) => setData("property_type", v as PropertyType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="house">Maison</SelectItem>
                      <SelectItem value="apartment">Appartement</SelectItem>
                      <SelectItem value="land">Terrain</SelectItem>
                      <SelectItem value="building">Immeuble</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="mixed">Mixte</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {current_user?.is_super_admin && (
                  <div className="space-y-2">
                    <Label htmlFor="organisation">Organisation</Label>
                    <Select value={data.organisation_id} onValueChange={(v) => setData("organisation_id", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner une organisation" />
                      </SelectTrigger>
                      <SelectContent>
                        {organisations.map((org) => (
                          <SelectItem key={org.id} value={String(org.id)}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="status">Statut</Label>
                    <Select value={data.status} onValueChange={(v) => setData("status", v as ProjectStatus)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">En attente</SelectItem>
                        <SelectItem value="accepted">Accepté</SelectItem>
                        <SelectItem value="rejected">Rejeté</SelectItem>
                        <SelectItem value="archived">Archivé</SelectItem>
                        <SelectItem value="asset">Actif</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pipe_status_id">Statut pipeline</Label>
                    <Select value={data.pipe_status_id} onValueChange={(v) => setData("pipe_status_id", v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {pipe_statuses.map((ps) => (
                          <SelectItem key={ps.id} value={ps.id}>
                            <span className="flex items-center gap-2">
                              <span className="inline-block size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: ps.text_color }} />
                              {ps.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lead_score">Score (0-10)</Label>
                  <Input
                    id="lead_score"
                    type="number"
                    min={0}
                    max={10}
                    value={data.lead_score}
                    onChange={(e) => setData("lead_score", parseInt(e.target.value) || 0)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="origin">Origine</Label>
                  <Select value={data.origin} onValueChange={(v) => setData("origin", v as ProjectOrigin)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kaptcher">Kaptcher</SelectItem>
                      <SelectItem value="terrafinder">Terrafinder</SelectItem>
                      <SelectItem value="creation">Création manuelle</SelectItem>
                      <SelectItem value="other_origin">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Localisation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="address">Adresse</Label>
                    <Input id="address" value={data.address} onChange={(e) => setData("address", e.target.value)} />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="zip_code">Code postal</Label>
                      <Input
                        id="zip_code"
                        value={data.zip_code}
                        onChange={(e) => setData("zip_code", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city_name">Ville</Label>
                      <Input
                        id="city_name"
                        value={data.city_name}
                        onChange={(e) => setData("city_name", e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={data.email}
                      onChange={(e) => setData("email", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone_number">Téléphone</Label>
                    <Input
                      id="phone_number"
                      value={data.phone_number}
                      onChange={(e) => setData("phone_number", e.target.value)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Surfaces</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="house_surface">Surface bâtie (m²)</Label>
                      <Input
                        id="house_surface"
                        type="number"
                        step="0.01"
                        value={data.house_surface}
                        onChange={(e) => setData("house_surface", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="field_surface">Surface terrain (m²)</Label>
                      <Input
                        id="field_surface"
                        type="number"
                        step="0.01"
                        value={data.field_surface}
                        onChange={(e) => setData("field_surface", e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Affectation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Utilisateurs</Label>
                <div className="flex flex-wrap gap-2">
                  {users.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => toggleArrayItem("assigned_user_ids", u.id)}
                      className={`rounded-md border px-3 py-1 text-sm transition-colors ${
                        data.assigned_user_ids.includes(u.id)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "hover:bg-muted"
                      }`}
                    >
                      {u.first_name} {u.last_name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Équipes</Label>
                <div className="flex flex-wrap gap-2">
                  {teams.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleArrayItem("assigned_team_ids", t.id)}
                      className={`rounded-md border px-3 py-1 text-sm transition-colors ${
                        data.assigned_team_ids.includes(t.id)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "hover:bg-muted"
                      }`}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-2">
                  {project_tags.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleArrayItem("project_tag_ids", tag.id)}
                      className={`rounded-md border px-3 py-1 text-sm transition-colors ${
                        data.project_tag_ids.includes(tag.id) ? "text-white border-transparent" : "hover:bg-muted"
                      }`}
                      style={data.project_tag_ids.includes(tag.id) ? { backgroundColor: tag.color } : {}}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3 pt-6">
            <Button variant="outline" asChild>
              <Link href={projectShowPath(project)}>Annuler</Link>
            </Button>
            <Button type="submit" disabled={processing}>
              Enregistrer
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}

ProjectEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default ProjectEdit
